import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { endDeploymentUploadRun } from "../../api/deploymentsApi";
import { DeploymentStatus } from "../common/ObjectTypes";
import tailwindClassnames from "../common/TailwindClassnames";

/**
 * Elements the Tab trap considers focusable. The same selector `ReportProblemModal` and
 * `RolePermissionsInfo` each carry as a module-private constant; neither exports it, and
 * exporting one would edit a shared component this ticket leaves alone.
 */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface EndUploadModalProps {
  isOpen: boolean;
  /** The deployment whose open upload run is to be ended. */
  deploymentId: number;
  /** The last file count the server reported for the run; omitted from the call when unknown. */
  fileCount?: number;
  /** What had focus when the dialog was asked for; focus returns here on close. */
  opener: HTMLElement | null;
  /**
   * Where focus goes on close when the opener is no longer in the document. After a
   * successful end the page refreshes the Last Event tile in the same render that closes this
   * dialog, so the End upload button — the opener — is already unmounted by the time focus is
   * handed back; focusing a detached element is a no-op and keyboard focus would fall to the
   * document body. A ref rather than an element, read only inside the close cleanup.
   */
  fallbackFocusRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Raised after the run was ended, so the page can refresh and toast. */
  onEnded: () => void;
  /**
   * Raised when the call came back 401 — the session is over and the interceptor has already
   * cleared the token. The page answers it by navigating to the app's session-expiry route,
   * which **unmounts this dialog**; nothing the user could do here can succeed, and without the
   * hand-off they would have to click a dead control once more just to trigger `authHeader()`'s
   * sign-out (Copilot review, cycles 9 and 10).
   *
   * Required, not optional: the whole 401 response is this navigation, so a caller that did not
   * wire it would leave the user in a dialog whose action can only fail. The type is the
   * enforcement.
   */
  onSessionExpired: () => void;
}

/**
 * The confirm dialog behind Media Management's "End upload" action (AKL-897, AC2 / AC3).
 *
 * Owns the `end_upload` call: confirming posts `failed` with the last known count through
 * `endDeploymentUploadRun`, which opts the request out of the interceptor's 403 and 5xx
 * handling so a refusal reaches this dialog instead of clearing the session. A 403 (AKL-895:
 * someone else's lock is live) and any other failure each show their own sentence beside the
 * buttons and leave the dialog open; success closes it and raises `onEnded`. A **401** is the one
 * outcome with no in-dialog state: it raises `onSessionExpired`, the page navigates to the
 * session-expiry route, and this dialog unmounts — the app's usual answer to a dead session.
 *
 * A real dialog to assistive technology, on the `ReportProblemModal` / `RolePermissionsInfo`
 * pattern rather than `ConfirmationModal`'s (AKL-883): `role="dialog"`, `aria-modal`, labelled
 * by its title and described by its body, focus moved to Cancel on open and back to the opener
 * on close (or to the page's fallback when the opener has gone), Tab wrapped inside the panel,
 * Escape / × / Cancel / backdrop all closing it — none of them while the request is in flight.
 */
const EndUploadModal: React.FC<EndUploadModalProps> = ({
  isOpen,
  deploymentId,
  fileCount,
  opener,
  fallbackFocusRef,
  onClose,
  onEnded,
  onSessionExpired,
}) => {
  const { t } = useTranslation();
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const wasEnding = useRef(false);
  // Mirrored into a ref so the open-transition effect can read it without listing it as a
  // dependency, which would re-run the effect (and re-focus Cancel) on every re-render that
  // hands in the trigger element afresh — the `openerRef` reasoning in ReportProblemModal.
  const openerRef = useRef<HTMLElement | null>(opener);
  useEffect(() => {
    openerRef.current = opener;
  }, [opener]);

  // Mirrored for the same reason as the opener: the cleanup must read the ref the page passed
  // in without the prop being a dependency of the open transition.
  const fallbackRef = useRef(fallbackFocusRef);
  useEffect(() => {
    fallbackRef.current = fallbackFocusRef;
  }, [fallbackFocusRef]);

  /**
   * The open transition: forget any earlier failure, move focus to Cancel — the safe control
   * of a destructive confirm — and, on close, hand focus back to what opened the dialog.
   *
   * When the opener has left the document by then — the success path, where the page's
   * refresh unmounts the End upload button in the same render that closes this dialog —
   * focus goes to the page's fallback instead, so it lands on something meaningful rather
   * than the document body (Copilot review, cycle 2).
   */
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setEnding(false);
    cancelRef.current?.focus();
    return () => {
      const opener = openerRef.current;
      const target =
        opener && opener.isConnected ? opener : fallbackRef.current?.current;
      target?.focus();
    };
  }, [isOpen]);

  /**
   * Where focus goes while the request is in flight, and after it.
   *
   * Disabling every control (which the pending state does) drops a browser's focus from the
   * confirm button to `<body>`, outside the dialog, and leaves the Tab trap with nothing to
   * hold — so focus is parked on the panel itself (`tabIndex={-1}`) the moment the request
   * starts, and the trap keeps it there while no control is enabled. When the request settles
   * with a failure, focus returns to the confirm button once it is enabled again; this has to
   * run after that re-render, because a still-disabled button ignores `focus()`. On success
   * the dialog closes in the same render, so the button is gone and the open-transition
   * cleanup hands focus back to the opener instead (Copilot review, cycle 1).
   *
   * A 401 needs nothing here: the page navigates away in the same continuation, so this dialog
   * unmounts rather than settling into a state that would need focus (Copilot review, cycle 10).
   */
  useEffect(() => {
    if (ending) {
      panelRef.current?.focus();
    } else if (wasEnding.current) {
      confirmRef.current?.focus();
    }
    wasEnding.current = ending;
  }, [ending]);

  const handleConfirm = async () => {
    if (ending) return;
    setEnding(true);
    setError(null);
    const outcome = await endDeploymentUploadRun(deploymentId, {
      status: DeploymentStatus.FAILED,
      ...(fileCount === undefined ? {} : { file_count: fileCount }),
    });
    if (outcome === "ended") {
      setEnding(false);
      onEnded();
      onClose();
      return;
    }
    setEnding(false);
    if (outcome === "refused") {
      setError(
        t(
          "This upload is still active, so it was not ended. Only the person uploading can end it while files are still arriving.",
        ),
      );
      return;
    }
    if (outcome === "unauthorized") {
      // The interceptor has already cleared the token, so "Please try again" would be a dead
      // end — every retry posts without an Authorization header and gets the same 401. Hand
      // straight off to the app's session-expiry route; the page navigates, which unmounts this
      // dialog, so there is deliberately no in-dialog message or disabled state here. Cycle 9
      // added both and cycle 10 showed they were unreachable: React batches the state updates
      // with the synchronous navigate in this same continuation, so nothing ever painted them.
      // Immediate redirect is also what the app does everywhere else for an expired session —
      // `ProfileProvider` and this page's own profile check both go straight to /unauthorized,
      // which renders the interceptor's `unauthorizedMessage` and the sign-in button.
      onSessionExpired();
      return;
    }
    setError(t("Could not end the upload. Please try again."));
  };

  /**
   * Escape and the Tab trap, both scoped to the panel (the `ReportProblemModal` handler, with
   * `RolePermissionsInfo`'s guard for focus that has left the controls).
   * @param event the keydown on the panel
   */
  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (ending) return;
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
    if (focusable.length === 0) {
      // Every control is disabled (the request is in flight): a Tab must still not leave the
      // dialog, so it stays on the panel.
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    // A click on the body text moves focus to the panel itself; an identity-only check would
    // then let the next Tab escape behind the dialog.
    if (!active || focusable.indexOf(active) === -1) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      // z-50: the confirm tier ConfirmationModal uses; modalOverlay carries no z-index by design.
      className={`${tailwindClassnames.modalOverlay} z-50`}
      onClick={(event) => {
        if (event.target !== event.currentTarget || ending) return;
        onClose();
      }}
      data-testid="end-upload-overlay"
    >
      <div
        ref={panelRef}
        className={`${tailwindClassnames.modalPanel} max-w-md`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-upload-title"
        aria-describedby="end-upload-description"
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        onClick={(event) => {
          // The Tab trap constrains Tab, not clicks: a click on the body text would move
          // focus to <body>. Keep it on the panel so a following Escape still reaches us.
          const target = event.target as HTMLElement;
          if (!target.closest(FOCUSABLE_SELECTOR)) panelRef.current?.focus();
        }}
        data-testid="end-upload-modal"
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className={tailwindClassnames.modalDangerIcon}
            >
              !
            </span>
            <h2 id="end-upload-title" className={tailwindClassnames.modalTitle}>
              {t("End upload")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
            disabled={ending}
            data-testid="end-upload-close"
          >
            &times;
          </button>
        </div>

        <div className={tailwindClassnames.modalBody}>
          <p id="end-upload-description">
            {t(
              "This upload has not sent any files for a while. Ending it lets uploads start again for this deployment.",
            )}
          </p>
          {error && (
            <div
              role="alert"
              className={`${tailwindClassnames.warningLabel} mt-3 inline-block`}
              data-testid="end-upload-error"
            >
              {error}
            </div>
          )}
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            ref={cancelRef}
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onClose}
            disabled={ending}
            data-testid="end-upload-cancel"
          >
            {t("Cancel")}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`relative ${tailwindClassnames.btnDanger}`}
            onClick={handleConfirm}
            disabled={ending}
            // The label below is hidden with `invisible` (visibility: hidden) while the
            // request runs so the button does not resize — which also drops it from the
            // accessible name, leaving the control nameless mid-request. An explicit
            // aria-label keeps the name stable in both states; unconditional, because the
            // value is byte-identical to the visible text and a name that comes and goes is
            // worse than a constant one (the AKL-752 accept-all pattern; Copilot, cycle 3).
            aria-label={t("End upload")}
            aria-busy={ending}
            data-testid="end-upload-confirm"
          >
            {/* The label stays in layout while ending so the button keeps its width; the
                spinner uses currentColor so it reads on the danger fill in both themes. */}
            <span className={ending ? "invisible" : ""}>{t("End upload")}</span>
            {ending && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EndUploadModal;
